import { Link, Icon, Box, FlexBox, Card, tokens, Typography, Button } from '@vp/swan';
import React, { useContext, useState, useEffect, useCallback } from 'react';
import { getFormattedTime } from './src-utils-miaw-common.js';
import './src-components-MessageComponent-MessageComponent.styles.scss.js';
import { CAContext } from './src-contexts-CAContext.js';
import MessageFeedback from './src-components-MessageFeedback-MessageFeedback.js';
import SignInComponent from './src-components-MessageComponent-SignInComponent.js';

const MessageComponentBase = ({
  messageContent,
  id,
  messageType,
  footer,
  additionalComponents,
  showFeedback,
  isMessageRead = true,
  isLastMessage = false,
  disableTypingEffect = false,
  onTypingComplete,
  urlMapping,
  attachments,
  formatType
}) => {
  const {
    chatState: { scrollToBottom, chatWindow }
  } = useContext(CAContext);
  const formattedTime = footer?.timeStamp ? getFormattedTime(footer.timeStamp) : "";
  const formattedAgentName = footer?.agentName ? `${footer.agentName} \xB7 ` : "";
  const [formattedMessageContent, setFormattedMessageContent] = useState("");
  const [isTypingCompleted, setIsTypingCompleted] = useState(false);
  const [bubbleWidth, setBubbleWidth] = useState(290);
  useEffect(() => {
    if (isTypingCompleted) {
      if (onTypingComplete) {
        onTypingComplete();
        scrollToBottom?.();
      }
    }
  }, [isTypingCompleted, onTypingComplete]);
  const typeMessageWithLinks = useCallback(
    (message, speed) => {
      const linkedParts = linkify(message);
      let partIndex = 0;
      let charIndex = 0;
      const renderedContent = [];
      let textBuffer = "";
      function processNextPart() {
        if (partIndex >= linkedParts.length) {
          const bubbleElement = document.getElementById(`caMessageBubble-${id}`);
          if (bubbleElement) {
            setBubbleWidth(bubbleElement.offsetWidth);
          }
          setIsTypingCompleted(true);
          return;
        }
        const currentPart = linkedParts[partIndex];
        if (React.isValidElement(currentPart) && (currentPart.type === Link || currentPart.type === Button)) {
          if (textBuffer) {
            renderedContent.push(textBuffer);
            textBuffer = "";
          }
          renderedContent.push(currentPart);
          setFormattedMessageContent([...renderedContent]);
          scrollToBottom?.();
          partIndex++;
          setTimeout(processNextPart, speed * 2);
        } else if (React.isValidElement(currentPart) && currentPart.type === React.Fragment) {
          let typeNextChar = function() {
            if (charIndex < textContent.length) {
              textBuffer += textContent[charIndex];
              setFormattedMessageContent([...renderedContent, textBuffer]);
              scrollToBottom?.();
              charIndex++;
              setTimeout(typeNextChar, speed);
            } else {
              if (textBuffer) {
                renderedContent.push(textBuffer);
                textBuffer = "";
              }
              charIndex = 0;
              partIndex++;
              setTimeout(processNextPart, speed);
            }
          };
          const fragmentProps = currentPart.props;
          const textContent = fragmentProps.children || "";
          typeNextChar();
        } else if (Array.isArray(currentPart)) {
          let processArrayParts = function(arrayIndex) {
            if (arrayIndex >= currentPart?.length) {
              partIndex++;
              setTimeout(processNextPart, speed);
              return;
            }
            const arrayElement = currentPart?.[arrayIndex];
            if (React.isValidElement(arrayElement) && typeof arrayElement.type === "string" && arrayElement.type === "b") {
              renderedContent.push(arrayElement);
              setFormattedMessageContent([...renderedContent]);
              scrollToBottom?.();
              arrayIndex++;
              setTimeout(() => processArrayParts(arrayIndex), speed);
            } else if (React.isValidElement(arrayElement) && arrayElement.type === React.Fragment) {
              let typeArrayFragmentChar = function() {
                if (fragmentCharIndex < textContent.length) {
                  textBuffer += textContent[fragmentCharIndex];
                  setFormattedMessageContent([...renderedContent, textBuffer]);
                  scrollToBottom?.();
                  fragmentCharIndex++;
                  setTimeout(typeArrayFragmentChar, speed);
                } else {
                  if (textBuffer) {
                    renderedContent.push(textBuffer);
                    textBuffer = "";
                  }
                  arrayIndex++;
                  setTimeout(() => processArrayParts(arrayIndex), speed);
                }
              };
              const fragmentProps = arrayElement.props;
              const textContent = fragmentProps.children || "";
              let fragmentCharIndex = 0;
              typeArrayFragmentChar();
            } else {
              renderedContent.push(arrayElement);
              setFormattedMessageContent([...renderedContent]);
              scrollToBottom?.();
              arrayIndex++;
              setTimeout(() => processArrayParts(arrayIndex), speed);
            }
          };
          if (textBuffer) {
            renderedContent.push(textBuffer);
            textBuffer = "";
          }
          processArrayParts(0);
        } else {
          renderedContent.push(currentPart);
          setFormattedMessageContent([...renderedContent]);
          scrollToBottom?.();
          partIndex++;
          setTimeout(processNextPart, speed);
        }
      }
      setFormattedMessageContent([]);
      processNextPart();
    },
    [id]
  );
  const linkify = useCallback((text) => {
    const urlRegex = /(\b(?:https?:\/\/|www\.)[^\s<>"]+[^\s<>"'.,!?;])/gi;
    const boldRegex = /\*\*([^*]+)\*\*/g;
    const extRegex = /<ext>([^<]*)<\/ext>/g;
    const extParts = text.split(extRegex);
    const processedParts = [];
    for (let i = 0; i < extParts.length; i++) {
      if (i % 2 === 0) {
        processedParts.push(extParts[i]);
      } else {
        processedParts.push(/* @__PURE__ */ React.createElement(SignInComponent, { key: `signin-${i}` }));
      }
    }
    return processedParts.map((part, partIndex) => {
      if (typeof part !== "string") {
        return part;
      }
      const urlParts = part.split(urlRegex);
      return urlParts.map((urlPart, index) => {
        if (urlPart.match(urlRegex)) {
          const href = urlPart.startsWith("http") ? urlPart : "https://" + urlPart;
          const urlLabel = urlMapping?.find((item) => item.url === href)?.label || urlPart;
          return /* @__PURE__ */ React.createElement(
            Link,
            {
              key: `${partIndex}-${index}`,
              to: href,
              withIcon: true,
              target: "_blank"
            },
            urlLabel,
            /* @__PURE__ */ React.createElement(Icon, { iconType: "externalLink", size: "20p" })
          );
        } else {
          if (boldRegex.test(urlPart)) {
            boldRegex.lastIndex = 0;
            const subParts = [];
            let lastIndex = 0;
            let match;
            while ((match = boldRegex.exec(urlPart)) !== null) {
              if (match.index > lastIndex) {
                subParts.push(/* @__PURE__ */ React.createElement(React.Fragment, { key: `${partIndex}-${index}-${lastIndex}` }, urlPart.substring(lastIndex, match.index)));
              }
              subParts.push(React.createElement("b", { key: `${partIndex}-${index}-bold-${match.index}` }, match[1]));
              lastIndex = match.index + match[0].length;
            }
            if (lastIndex < urlPart.length) {
              subParts.push(/* @__PURE__ */ React.createElement(React.Fragment, { key: `${partIndex}-${index}-last` }, urlPart.substring(lastIndex)));
            }
            return subParts;
          }
          return /* @__PURE__ */ React.createElement(React.Fragment, { key: `${partIndex}-${index}` }, urlPart);
        }
      });
    }).flat();
  }, []);
  useEffect(() => {
    let timeout;
    if (messageType === "chatbot" && typeof messageContent === "string" && !isMessageRead && !disableTypingEffect && chatWindow === "maximized") {
      timeout = setTimeout(() => {
        typeMessageWithLinks(messageContent, 10);
      }, 800);
    } else {
      setIsTypingCompleted(true);
      if (typeof messageContent === "string") {
        setFormattedMessageContent(linkify(messageContent));
      } else {
        setFormattedMessageContent(messageContent);
      }
    }
    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [messageContent, messageType]);
  const getAvatarContent = useCallback(() => {
    return footer?.agentName?.[0].toUpperCase() || "S";
  }, [footer]);
  const getIcon = useCallback(() => {
    if (messageType === "chatbot" && (isLastMessage || !isTypingCompleted)) {
      return /* @__PURE__ */ React.createElement(Icon, { iconType: "aiSupport", className: "ca-message-bubble-avatar", marginRight: 2 });
    } else if (messageType === "agent" && isLastMessage) {
      return /* @__PURE__ */ React.createElement(Box, { className: "ca-message-bubble-avatar-agent", fontSize: "small", fontWeight: "bold", marginRight: 2 }, getAvatarContent());
    } else {
      return /* @__PURE__ */ React.createElement(Box, { style: { width: "20px", height: "20px" }, marginRight: 2 });
    }
  }, [messageType, isLastMessage, isTypingCompleted]);
  function getAttachmentPreview(attachment) {
    const { mimeType } = attachment;
    let fileIconType = "fileGeneric";
    if (mimeType === "image/jpeg" || mimeType === "image/jpg") {
      fileIconType = "fileJpg";
    } else if (mimeType === "image/png") {
      fileIconType = "filePng";
    } else if (mimeType === "application/pdf") {
      fileIconType = "filePdf";
    }
    return /* @__PURE__ */ React.createElement(Icon, { iconType: fileIconType, size: "20p", style: { background: "black", margin: "14px" } });
  }
  return /* @__PURE__ */ React.createElement(FlexBox, { paddingBottom: messageContent == "" ? "0" : isLastMessage ? "6" : "2", compactMode: true, flexDirection: "column" }, (messageContent !== "" || attachments) && /* @__PURE__ */ React.createElement(
    FlexBox,
    null,
    /** Avatar to be displayed only for chatbot/agent message and only if single message is present */
    getIcon(),
    /* @__PURE__ */ React.createElement(
      Card,
      {
        className: `
              ca-message-bubble-base
              ca-message-bubble-${messageType}
              ${isLastMessage ? `ca-message-bubble-${messageType}-lastMessage` : ""}
              ${typeof messageContent !== "string" && messageType !== "agent" ? "ca-message-bubble-element" : ""}
              ${!isMessageRead ? `ca-message-bubble-${messageType}-animate` : ""}`,
        fullBleed: true,
        style: !isLastMessage ? { borderRadius: tokens.SwanSemBorderRadiusStrong } : {},
        darkMode: messageType === "user",
        compactMode: true,
        id: `caMessageBubble-${id}`,
        mb: attachments ? "4" : "0"
      },
      /* @__PURE__ */ React.createElement(Box, { className: `ca-message-bubble-message-content-base ca-message-bubble-message-content-${messageType}`, id: `caMessageContent-${id}` }, formatType && formatType == "Attachments" && attachments && /* @__PURE__ */ React.createElement(Card, { bordered: true, style: { backgroundColor: "white" }, display: "flex", p: "4" }, /* @__PURE__ */ React.createElement(Box, { width: "48px", height: "48px", style: { borderRadius: "12px" } }, getAttachmentPreview(attachments[0])), /* @__PURE__ */ React.createElement(Box, { ml: "4" }, /* @__PURE__ */ React.createElement(FlexBox, { style: { height: "100%" }, justifyContent: "center", alignItems: "center" }, /* @__PURE__ */ React.createElement(Typography, { fontSkin: "body-standard-bold", style: { color: "black" } }, attachments[0].name)))), formattedMessageContent),
      footer?.isVisible && isLastMessage && /* @__PURE__ */ React.createElement(Box, { className: `ca-message-bubble-footer-${messageType}`, paddingTop: "4" }, formattedAgentName, formattedTime)
    )
  ), isTypingCompleted && showFeedback && messageType === "chatbot" && /* @__PURE__ */ React.createElement(
    MessageFeedback,
    {
      bubbleWidth,
      messageId: id.split("__")[0]
    }
  ), isTypingCompleted && /* @__PURE__ */ React.createElement(Box, { style: { paddingLeft: "24px", maxWidth: "90%" }, className: "ca-message-additional-component" }, additionalComponents));
};

export { MessageComponentBase as default };
//# sourceMappingURL=src-components-MessageComponent-MessageComponentBase.js.map
